import { ApiClientService, EntityApiService } from '@dfl/react-security';
import fileDownload from 'js-file-download';
import { isEmpty } from 'lodash';
import { format } from 'date-fns';
import { ICommonDownload } from 'modules/export/interfaces/common-export';

// Global download lock to prevent duplicate downloads
const downloadLocks = new Map<string, number>();

const checkDownloadLock = (downloadId: string, cooldownMs: number = 2000): boolean => {
  const now = Date.now();
  const lastDownload = downloadLocks.get(downloadId);

  if (lastDownload && now - lastDownload < cooldownMs) {
    return false; // Block download
  }

  downloadLocks.set(downloadId, now);

  // Clean up old locks (older than 10 seconds)
  downloadLocks.forEach((timestamp, id) => {
    if (now - timestamp > 10000) {
      downloadLocks.delete(id);
    }
  });

  return true; // Allow download
};

export class CommonExport<T> extends EntityApiService<T> {
  getExportPath = (path = '', params = {}): Promise<any> => {
    return this.handleResponse(ApiClientService.post(this.getPath(path ?? '/export'), params));
  };

  generateDownload = (path: string): Promise<any> => {
    return this.handleResponse(ApiClientService.get(this.getPath(path)));
  };

  downloadFileToExcel = ({ name, path }: ICommonDownload) => {
    // Ensure name has .xlsx extension
    const fileName = name.endsWith('.xlsx') ? name : `${name}.xlsx`;

    // Create download ID from path and name
    const downloadId = `${path}_${fileName}`;

    // Check if this download was already attempted recently
    if (!checkDownloadLock(downloadId, 2000)) {
      return Promise.resolve(); // Skip duplicate download
    }

    return ApiClientService.get(this.getPath(path), { responseType: 'blob' }).then(({ data }) => {
      fileDownload(data, fileName);
    });
  };

  downloadFileToPdf = ({ name, path }: ICommonDownload) => {
    const time = format(new Date(), 'dd-MM-yyyy');
    const _name = `${name}_${time}.pdf`;

    // Create download ID from path and name
    const downloadId = `${path}_${_name}`;

    // Check if this download was already attempted recently
    if (!checkDownloadLock(downloadId, 2000)) {
      return Promise.resolve(); // Skip duplicate download
    }

    return ApiClientService.get(this.getPath(path), { responseType: 'blob' }).then(({ data }) => {
      fileDownload(data, _name);
    });
  };

  _downloadDocument = (
    basePath: string,
    name: string,
    filters?: any,
    search?: string,
    provider?: string,
    segment?: string,
  ) => {
    const query: any = {};
    if (!isEmpty(filters)) query.filters = filters;
    if (search) query.search = search;
    query.segment = segment;
    if (provider) query.provider = provider;
    return ApiClientService.post(this.getPath(basePath), query, {
      responseType: 'blob',
    }).then(({ data }) => {
      fileDownload(data, name || 'exportExcel.xlsx');
    });
  };

  _exportToPDF = ({
    name = 'suborders-users',
    filters,
    path = '/pdfBulk?admin=true',
  }: {
    filters?: any;
    name?: string;
    path?: string;
  }) => {
    const time = format(new Date(), 'dd-MM-yyyy');
    const _name = `${name}-${time}.pdf`;
    return ApiClientService.post(this.getPath(path), { filters }, { responseType: 'blob' }).then(({ data }) => {
      fileDownload(data, _name);
    });
  };
}
